import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const LOGO_URL =
  'https://storage.readdy-site.link/project_files/66c63773-f4df-4b51-a232-b85a9487d8fb/a97ed8bf-79aa-4d6a-a5e7-b55b88780d52_compressed_Penland-LLC-Logo.webp';

const navLinks = [
  { label: 'Home', to: '/' },
  { label: 'Bookkeeping', to: '/bookkeeping' },
  { label: 'Consulting', to: '/consulting' },
  { label: 'Properties', to: '/properties' },
  { label: 'MatchLineup', to: '/matchlineup' },
  { label: 'Alabaster', to: 'https://alabastertravel.com/', external: true },
];

interface SecondaryNav {
  links: { label: string; href: string }[];
  cta?: { label: string; href: string };
}

export default function SiteHeader({ secondary }: { secondary?: SecondaryNav }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (location.hash) {
      const handler = window.setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 140);
      return () => window.clearTimeout(handler);
    }
  }, [location.hash, location.pathname]);

  const scrollTo = (href: string) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const lightText = !scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background-50/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20 md:h-24">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img
              src={LOGO_URL}
              alt="Penland LLC logo"
              className={`h-12 w-auto object-contain transition-[filter] duration-300 ${
                lightText ? 'brightness-0 invert' : ''
              }`}
            />
            <span
              className={`font-heading text-lg md:text-xl font-semibold tracking-tight transition-colors ${
                lightText ? 'text-background-50' : 'text-primary-950'
              }`}
            >
              Penland LLC
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-6">
            {navLinks.map((link) =>
              link.external ? (
                <a
                  key={link.to}
                  href={link.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-sm font-medium tracking-wide transition-colors hover:text-accent-500 whitespace-nowrap ${
                    lightText ? 'text-background-100' : 'text-foreground-700'
                  }`}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-sm font-medium tracking-wide transition-colors hover:text-accent-500 whitespace-nowrap ${
                    lightText ? 'text-background-100' : 'text-foreground-700'
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/#contact"
              onClick={(e) => {
                const el = document.querySelector('#contact');
                if (el) {
                  e.preventDefault();
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className="hidden md:inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-primary-950 text-sm font-semibold px-5 py-2.5 rounded-full transition-all whitespace-nowrap"
            >
              Contact Us
            </Link>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`xl:hidden p-2 rounded-lg transition-colors ${
                lightText ? 'text-background-50' : 'text-primary-950'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* Secondary row (Bookkeeping page section links) */}
      {secondary && (
        <div
          className={`border-t transition-colors ${
            lightText ? 'border-background-50/15' : 'border-background-200'
          }`}
        >
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="flex items-center gap-5 md:gap-7 h-11 overflow-x-auto whitespace-nowrap">
              {secondary.links.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollTo(link.href)}
                  className={`text-xs md:text-sm font-medium tracking-wide transition-colors hover:text-accent-500 ${
                    lightText ? 'text-background-200' : 'text-foreground-600'
                  }`}
                >
                  {link.label}
                </button>
              ))}
              {secondary.cta && (
                <button
                  onClick={() => scrollTo(secondary.cta!.href)}
                  className="ml-auto inline-flex items-center gap-1.5 bg-accent-500 hover:bg-accent-600 text-primary-950 text-xs md:text-sm font-semibold px-4 py-1.5 rounded-full transition-all whitespace-nowrap"
                >
                  {secondary.cta.label}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="xl:hidden bg-background-50 border-t border-background-200 shadow-lg">
          <div className="px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) =>
              link.external ? (
                <a
                  key={link.to}
                  href={link.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-left text-foreground-700 font-medium py-3 px-2 rounded-lg hover:bg-background-100 transition-colors"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-left text-foreground-700 font-medium py-3 px-2 rounded-lg hover:bg-background-100 transition-colors"
                >
                  {link.label}
                </Link>
              )
            )}
            {secondary && (
              <div className="flex flex-wrap gap-x-5 gap-y-2 py-3 px-2 border-t border-background-200 mt-1">
                {secondary.links.map((link) => (
                  <button
                    key={link.href}
                    onClick={() => scrollTo(link.href)}
                    className="text-foreground-600 text-sm font-medium"
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            )}
            <Link
              to="/#contact"
              onClick={(e) => {
                const el = document.querySelector('#contact');
                if (el) {
                  e.preventDefault();
                  setMobileOpen(false);
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className="mt-2 w-full text-center bg-accent-500 hover:bg-accent-600 text-primary-950 text-sm font-semibold px-5 py-3 rounded-full transition-all"
            >
              Contact Us
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}